/**
 * Origin pubblici delle superfici amministrative MASTER ed EasyCRM.
 *
 * Il master NON è un prodotto del PRODUCT_REGISTRY (superficie tenant-agnostic),
 * quindi ha una env dedicata invece di una entry in `PRODUCT_PUBLIC_ORIGINS`:
 *
 *   MASTER_PUBLIC_BASE_URL — base URL pubblica dove è servita la SPA master,
 *   SENZA trailing slash. Due forme supportate:
 *     - `https://master.<BRAND_BASE_DOMAIN>`  (target: sottodominio, SPA alla root)
 *     - `https://<apex>/master`               (legacy: path sull'apex, in transizione)
 *
 * La env viene riletta AD OGNI chiamata (nessuno snapshot al boot), come
 * `product-domains.ts`. Default senza env: `https://master.${BRAND_BASE_DOMAIN}`.
 * Lato browser (Vite) `process.env` non esiste → vale sempre il default,
 * derivato dal brand e mai hardcodato nei call-site.
 *
 * EasyCRM è una superficie amministrativa separata, ma con la stessa identità
 * master: `CRM_PUBLIC_BASE_URL` (default `https://crm.<brand>`). Non è un
 * prodotto tenant e non usa il PRODUCT_REGISTRY.
 *
 * NON usare per il piano SIP (resta `BRAND_BASE_DOMAIN`) né per gli origin
 * tenant per-prodotto (`product-domains.ts`).
 */

import { BRAND_BASE_DOMAIN } from './branding';

const ENV_KEY = 'MASTER_PUBLIC_BASE_URL';
const CRM_ENV_KEY = 'CRM_PUBLIC_BASE_URL';

/** Base URL valida = origin https/http + path opzionale, senza trailing slash. */
function normalizeBaseUrl(raw: string): string | null {
  const v: string = raw.trim().replace(/\/+$/, '');
  if (!/^https?:\/\/[a-z0-9.-]+(:\d+)?(\/[a-z0-9._~-]+)*$/i.test(v)) return null;
  return v;
}

function readEnvBaseUrl(envKey: string): string | null {
  const raw: string | undefined =
    typeof process !== 'undefined' && process.env ? process.env[envKey] : undefined;
  if (!raw) return null;
  return normalizeBaseUrl(raw);
}

/** Origin target del sottodominio dedicato, derivato dal brand. */
export function getDefaultMasterOrigin(): string {
  return `https://master.${BRAND_BASE_DOMAIN}`;
}

/** Origin target di EasyCRM, derivato dal brand e condiviso da FE e BE. */
export function getDefaultCrmOrigin(): string {
  return `https://crm.${BRAND_BASE_DOMAIN}`;
}

/**
 * Base URL pubblica della SPA master (senza trailing slash). I path SPA vanno
 * appesi root-relative: `buildMasterUrl('/login')`.
 *
 * Risoluzione: env `MASTER_PUBLIC_BASE_URL` → `fallbackPlatformBaseUrl`
 * (se fornito, con suffisso `/master` legacy) → default sottodominio brand.
 * Il fallback serve ai chiamanti backend che oggi risolvono il dominio
 * piattaforma runtime (`getPublicBaseUrl()`): a env assente in ambienti non
 * ancora migrati i link restano su `<piattaforma>/master` come prima.
 */
export function getMasterPublicBaseUrl(fallbackPlatformBaseUrl?: string): string {
  const fromEnv = readEnvBaseUrl(ENV_KEY);
  if (fromEnv) return fromEnv;
  if (fallbackPlatformBaseUrl) {
    const fb = normalizeBaseUrl(fallbackPlatformBaseUrl);
    if (fb) return `${fb}/master`;
  }
  return getDefaultMasterOrigin();
}

/** Base URL pubblica della SPA EasyCRM, sempre alla root del proprio host. */
export function getCrmPublicBaseUrl(): string {
  return readEnvBaseUrl(CRM_ENV_KEY) ?? getDefaultCrmOrigin();
}

/** URL completa verso una pagina della SPA master (path root-relative, es. `/crm`). */
export function buildMasterUrl(path: string, fallbackPlatformBaseUrl?: string): string {
  const p: string = path.startsWith('/') ? path : `/${path}`;
  return `${getMasterPublicBaseUrl(fallbackPlatformBaseUrl)}${p}`;
}

/** URL completa verso una pagina della SPA EasyCRM (path root-relative). */
export function buildCrmUrl(path: string): string {
  const p: string = path.startsWith('/') ? path : `/${path}`;
  return `${getCrmPublicBaseUrl()}${p}`;
}

/** Origin (scheme://host[:port]) della base URL master configurata. */
export function getMasterPublicOrigin(): string {
  const base = getMasterPublicBaseUrl();
  try {
    return new URL(base).origin;
  } catch {
    return getDefaultMasterOrigin();
  }
}

/** Origin (scheme://host[:port]) della base URL EasyCRM configurata. */
export function getCrmPublicOrigin(): string {
  try {
    return new URL(getCrmPublicBaseUrl()).origin;
  } catch {
    return getDefaultCrmOrigin();
  }
}

/**
 * Origin master fidati per l'allowlist condivisa (CORS Express/Socket.IO,
 * trust `pk_`). Include sempre il target di brand oltre all'eventuale origin
 * da env, così la transizione env→default non apre buchi di trust.
 */
export function getMasterTrustedOrigins(): ReadonlyArray<string> {
  return Array.from(new Set([getMasterPublicOrigin(), getDefaultMasterOrigin()]));
}

/** True se `origin` è un origin master fidato (match esatto, case-insensitive). */
export function isTrustedMasterOrigin(origin: string): boolean {
  const o: string = origin.trim().replace(/\/+$/, '').toLowerCase();
  return getMasterTrustedOrigins().some((t) => t.toLowerCase() === o);
}

/** Origin EasyCRM fidati per CORS, Socket.IO e controlli BFF. */
export function getCrmTrustedOrigins(): ReadonlyArray<string> {
  return Array.from(new Set([getCrmPublicOrigin(), getDefaultCrmOrigin()]));
}

export function isTrustedCrmOrigin(origin: string): boolean {
  const o = origin.trim().replace(/\/+$/, '').toLowerCase();
  return getCrmTrustedOrigins().some((trusted) => trusted.toLowerCase() === o);
}

/** Superfici amministrative servite dallo stesso frontend, ma isolate per host. */
export type AdminSurface = 'master' | 'crm';

export interface AdminSurfaceContext {
  surface: AdminSurface;
  loginPath: string;
  homePath: string;
  crmRoutePrefix: string;
}

/**
 * Risolve la superficie dalla coppia host/path senza alcun tenant slug.
 * Il pathname conserva la compatibilità del build legacy `/master/crm`.
 */
export function resolveAdminSurface(hostname: string | undefined, pathname = '/'): AdminSurface {
  const normalizedHost = hostname?.trim().toLowerCase().replace(/:\d+$/, '') ?? '';
  const crmHosts = new Set(
    getCrmTrustedOrigins().flatMap((origin) => {
      try {
        return [new URL(origin).hostname.toLowerCase()];
      } catch {
        return [];
      }
    }),
  );

  if (crmHosts.has(normalizedHost)) return 'crm';
  return /^\/(?:master\/)?crm(?:\/|$)/.test(pathname) ? 'crm' : 'master';
}

/**
 * Definisce tutte le destinazioni SPA di una superficie. `legacyCrmPath` è
 * limitato al vecchio build sotto `/master`; sui nuovi host entrambe le SPA
 * vivono alla root e EasyCRM non eredita percorsi della console Master.
 */
export function getAdminSurfaceContext(
  surface: AdminSurface,
  options: { legacyCrmPath?: boolean } = {},
): AdminSurfaceContext {
  if (surface === 'crm') {
    const crmRoutePrefix = options.legacyCrmPath ? '/crm' : '';
    return {
      surface,
      loginPath: `${crmRoutePrefix}/login`,
      homePath: crmRoutePrefix || '/',
      crmRoutePrefix,
    };
  }

  return {
    surface,
    loginPath: '/login',
    homePath: '/dashboard',
    crmRoutePrefix: '/crm',
  };
}

/** Allowlist completa per tutte le superfici amministrative host-only. */
export function getAdminTrustedOrigins(): ReadonlyArray<string> {
  return Array.from(new Set([...getMasterTrustedOrigins(), ...getCrmTrustedOrigins()]));
}

export function isTrustedAdminOrigin(origin: string): boolean {
  const o = origin.trim().replace(/\/+$/, '').toLowerCase();
  return getAdminTrustedOrigins().some((trusted) => trusted.toLowerCase() === o);
}
